import type {
  PermissionGrants,
  PermissionKey,
} from "~/layers/roles/server/core/types";
import type {
  AndRequirement,
  NotRequirement,
  AuthenticatedRequirement,
  AuthRequirement,
  OrRequirement,
  PermissionRequirement,
  ConditionalRequirement,
  Accessor,
} from "../types";

export const AuthRequirementBuilder = {
  authenticated: (value: boolean = true): AuthenticatedRequirement => ({
    type: "authenticated",
    value,
  }),
  permissions: (
    permissions: PermissionKey[],
    mode: "all" | "any" = "all"
  ): PermissionRequirement => ({
    type: "permission",
    permissions,
    mode,
  }),
  and: (...requirements: (AuthRequirement | undefined)[]): AndRequirement => ({
    type: "and",
    requirements: requirements.filter(
      (requirement): requirement is AuthRequirement => requirement !== undefined
    ),
  }),
  or: (...requirements: (AuthRequirement | undefined)[]): OrRequirement => ({
    type: "or",
    requirements: requirements.filter(
      (requirement): requirement is AuthRequirement => requirement !== undefined
    ),
  }),
  not: (requirement: AuthRequirement): NotRequirement => ({
    type: "not",
    requirement,
  }),
  conditional: (
    condition: (accessor: Accessor) => boolean
  ): ConditionalRequirement => ({
    type: "conditional",
    condition,
  }),
};

export function hasAccess(
  accessor: Accessor,
  requirement: AuthRequirement
): boolean {
  switch (requirement.type) {
    case "authenticated":
      return requirement.value === accessor.isAuthenticated;

    case "permission":
      return requirement.mode === "all"
        ? requirement.permissions.every(
            (perm) => accessor.grants?.[perm]?.grant ?? false
          )
        : requirement.permissions.some(
            (perm) => accessor.grants?.[perm]?.grant ?? false
          );

    case "and":
      return requirement.requirements.every((req) => hasAccess(accessor, req));

    case "or":
      return requirement.requirements.some((req) => hasAccess(accessor, req));

    case "not":
      return !hasAccess(accessor, requirement.requirement);

    case "conditional":
      return requirement.condition(accessor);
  }
}
